import { getQuoteConversionBlockReason } from '../utils/poSales';
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faEdit, faTrash, faExchangeAlt, faChevronLeft, faChevronRight, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import { Button, Form } from "@themesberg/react-bootstrap";
import { Link, useInRouterContext } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL, END_POINT_QUOTES } from '../const';
import { hasUserPermission } from '../utils/userPermissions';
import { buildQuotePayload, buildQuoteProducts } from '../utils/quoteProducts';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import QuoteProductsSummary from './QuoteProductsSummary';
import './QuoteDetailProductsPanel.css';

const QUOTE_STATUSES = ['Closed', 'Live', 'Lost', 'Won'];

const relatedId = (quote, field, nested) => quote?.[field] ?? quote?.[nested]?.id ?? '';

const quoteStatusPayload = (quote, status) => buildQuotePayload({
  system_quote_no: quote.system_quote_no || '',
  offer_date: quote.offer_date || '',
  customer_id: relatedId(quote, 'customer_id', 'customer'),
  place: quote.place || '',
  company_id: relatedId(quote, 'company_id', 'company'),
  branch_id: relatedId(quote, 'branch_id', 'branch'),
  principal_id: relatedId(quote, 'principal_id', 'principal'),
  products: buildQuoteProducts(quote),
  category_id: relatedId(quote, 'category_id', 'category'),
  se_id: relatedId(quote, 'se_id', 'salesExecutive') || relatedId(quote, 'se_id', 'sales_executive'),
  expected_date: quote.expected_date || '',
  status,
  remarks: quote.remarks || '',
});

// PO Sales No.: opens the PO Sales Listing with that record's detail panel already open.
const PoSaleLink = ({ id, label }) => {
  const inRouter = useInRouterContext();
  if (!inRouter || !hasUserPermission('po_sales', 'view')) return label;
  return <Link to="/po-sales" state={{ openPoSaleId: id }} className="po-quote-link" title="Open this PO Sales record">{label}</Link>;
};

const DetailRow = ({ label, value }) => (
  <div className="quote-detail-row">
    <div className="quote-detail-label">{label}</div>
    <div className="quote-detail-value">{value || value === 0 ? value : "--"}</div>
  </div>
);

export const QuoteDetailSidebar = ({
  quote,
  show,
  onClose,
  onEdit,
  onDelete,
  onConvertToPoSales,
  canDelete,
  canManage = false,
  currentUserId = null,
  canEdit = false,
  canConvert = false,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  loading = false,
  error = '',
  onRetry,
  onStatusSaved,
}) => {

  const [productsQuoteId, setProductsQuoteId] = useState(null);
  const [draftStatus, setDraftStatus] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const quoteId = quote?.id;
  const currentStatus = quote?.status || '';
  const statusLocked = Boolean(quote?.po_converted_at || quote?.has_po_sale);
  // Editing is open to everyone who can see the quote; delete/convert stay with the owner or creator.
  const canChangeStatus = canEdit && !loading && !error && !!quote;
  useEffect(() => { setProductsQuoteId(null); }, [quoteId, show]);
  useEffect(() => {
    setDraftStatus(currentStatus);
    setSavingStatus(false);
  }, [quoteId, currentStatus, show]);
  const showProducts = show && quoteId != null && productsQuoteId === quoteId && !loading && !error;

  useEffect(() => {
    if (!showProducts) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setProductsQuoteId(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [showProducts]);

  // The user the quote is assigned to may convert it, even when someone else created it.
  const isAssignee = currentUserId != null && quote?.se_id != null && String(quote.se_id) === String(currentUserId);
  const showConvert = canConvert && !loading && !error && !getQuoteConversionBlockReason(quote);

  return (
    <>
      <div
        className={`quote-detail-backdrop ${show ? "show" : ""}`}
        onClick={onClose}
      />
      <div className={`quote-detail-panel ${show ? "show" : ""} ${showProducts ? "with-products" : ""}`}>
        {quote && (
          <>
            <div className="quote-detail-header">
              <h5 className="mb-0">Quote Details</h5>
              <div className="d-flex align-items-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="me-2"
                  disabled={!hasPrevious}
                  onClick={onPrevious}
                  title="Previous quote"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                  Prev
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="me-3"
                  disabled={!hasNext}
                  onClick={onNext}
                  title="Next quote"
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                </Button>
                <Button variant="link" className="p-0 text-dark" aria-label="Close quote details" onClick={onClose}>
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </div>
            </div>

            <div className="quote-detail-body" aria-busy={loading}>
              {loading && <div role="status" className="mb-3 text-muted">Loading quote details...</div>}
              {error && <div role="alert" className="alert alert-danger">
                {error} <Button size="sm" variant="outline-danger" onClick={onRetry}>Retry details</Button>
              </div>}
              {/* <DetailRow label="SR No." value={quote.sr_no} /> */}
              <DetailRow label="Offer No." value={quote.offer_no} />
              <DetailRow label="PO Number" value={quote.po_sale_id ? <PoSaleLink id={quote.po_sale_id} label={quote.po_sale_number || 'PO Sales #' + quote.po_sale_id} /> : null} />
              <DetailRow label="System Quote No." value={quote.system_quote_no} />
              <DetailRow label="Offer Date" value={quote.offer_date} />
              <DetailRow label="Customer" value={quote.customer?.name} />
              <DetailRow label="Place" value={quote.place} />
              <DetailRow label="Company" value={quote.company?.name} />
              <DetailRow label="Branch" value={quote.branch?.name} />
              <DetailRow label="Principal" value={quote.principal?.name} />
              <DetailRow label="Category" value={quote.category?.name} />
              <div className="quote-detail-row">
                <div className="quote-detail-label">Products</div>
                <Button type="button" variant="outline-primary" size="sm"
                  aria-expanded={!!showProducts}
                  disabled={!show || quoteId == null || loading || !!error}
                  onClick={() => setProductsQuoteId(quoteId)}>
                  <FontAwesomeIcon icon={faBoxOpen} className="me-2" />View Products
                </Button>
              </div>
              <DetailRow label="Quantity" value={quote.qty} />
              <DetailRow
                label="Total"
                value={`₹ ${Number(quote.total || 0).toLocaleString("en-IN")}`}
              />
              <DetailRow label="Sales Executive" value={quote.sales_executive?.name} />
              <DetailRow label="Expected Date" value={quote.expected_date} />
              <div className="quote-detail-row">
                <div className="quote-detail-label">Status</div>
                {canChangeStatus ? (
                  <div className="quote-detail-status">
                    <Form.Select
                      size="sm"
                      aria-label="Quote status"
                      value={draftStatus || currentStatus}
                      disabled={statusLocked || savingStatus}
                      onChange={event => setDraftStatus(event.target.value)}
                    >
                      {QUOTE_STATUSES.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </Form.Select>
                    {draftStatus && draftStatus !== currentStatus && (
                      <Button type="button" variant="success" size="sm"
                        disabled={savingStatus}
                        onClick={async () => {
                          const confirmation = await Swal.fire({
                            icon: 'warning',
                            title: 'Change quote status?',
                            text: `Change status from ${currentStatus || 'the current value'} to ${draftStatus}?`,
                            showCancelButton: true,
                            confirmButtonText: 'Yes',
                            cancelButtonText: 'No',
                          });
                          if (!confirmation.isConfirmed) {
                            setDraftStatus(currentStatus);
                            return;
                          }
                          const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
                          setSavingStatus(true);
                          try {
                            const response = await axios.put(
                              `${API_URL + END_POINT_QUOTES}/${quoteId}`,
                              quoteStatusPayload(quote, draftStatus),
                              { headers: { Accept: 'application/json', Authorization: 'Bearer ' + token } }
                            );
                            showSuccessToast(response.data.message || 'Quote updated successfully');
                            onStatusSaved?.(response.data.data);
                          } catch (failure) {
                            showErrorToast(failure.response?.data?.message || 'Failed to update quote status.');
                            setSavingStatus(false);
                          }
                        }}>
                        {savingStatus ? 'Saving...' : 'Save'}
                      </Button>
                    )}
                    {statusLocked && <div className="quote-detail-status-note">Converted quotes remain Won.</div>}
                  </div>
                ) : (
                  <div className="quote-detail-value">{quote.status || '--'}</div>
                )}
              </div>
              <DetailRow label="Remarks" value={quote.remarks} />
              {quote.was_transferred && (
                <div className="quote-ownership-history">
                  <div className="quote-detail-label mb-2">Ownership History</div>
                  {(quote.ownership_transfers || []).map(transfer => (
                    <div key={transfer.id} className="quote-detail-row">
                      <div className="quote-detail-value text-start">
                        Transferred from {transfer.from_user?.name || 'Unknown'} to {transfer.to_user?.name || 'Unknown'}
                        {transfer.transferred_by?.name ? ` by ${transfer.transferred_by.name}` : ''}
                        {transfer.transferred_at ? ` on ${new Date(transfer.transferred_at).toLocaleString()}` : ''}
                        {transfer.reason ? `. Reason: ${transfer.reason}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* {canManage && (canEdit || canDelete || canConvert) && <div className="quote-detail-footer">
              {canConvert && <Button variant="outline-primary" onClick={() => onConvertToPoSales(quote.id)}> */}
            {(canEdit || ((canManage || isAssignee) && showConvert) || ((canManage || isAssignee) && canDelete)) && <div className="quote-detail-footer">
              {(canManage || isAssignee) && showConvert && <Button variant="outline-primary" size="sm" onClick={() => onConvertToPoSales(quote.id)}>
                <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />Convert to PO Sales
              </Button>}
              {canEdit && <Button variant="success" size="sm" onClick={() => onEdit(quote.id)}>
                <FontAwesomeIcon icon={faEdit} className="me-2" />Edit
              </Button>}
              {(canManage || isAssignee) && canDelete && <Button variant="danger" size="sm" onClick={() => onDelete(quote.id)}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />Delete
              </Button>}
            </div>}
          </>
        )}
      </div>

      {showProducts && <aside className="quote-products-panel" aria-label="Quote Products">
        <div className="quote-detail-header">
          <div>
            <h5 className="mb-0">Quote Products</h5>
            {quote?.offer_no && <div className="quote-detail-label mt-1">Offer No. {quote.offer_no}</div>}
          </div>
          <Button type="button" variant="link" className="p-0 text-dark"
            aria-label="Close products" onClick={() => setProductsQuoteId(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
        <div className="quote-detail-body">
          <QuoteProductsSummary quote={quote} showHeading={false} />
        </div>
      </aside>}

      <style> {` .quote-detail-backdrop { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.35); opacity: 0; pointer-events: none; transition: opacity 0.25s ease; z-index: 1040; } .quote-detail-backdrop.show { opacity: 1; pointer-events: auto; } .quote-detail-panel { position: fixed; top: 0; right: 0; bottom: 0; width: 600px; max-width: 100%; background: #fff; box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15); transform: translateX(100%); transition: transform 0.25s ease; z-index: 1050; display: flex; flex-direction: column; } .quote-detail-panel.show { transform: translateX(0); } .quote-detail-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid #e9ecef; } .quote-detail-body { flex: 1; overflow-y: auto; padding: 1rem 1.25rem; } .quote-detail-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; padding: 0.5rem 0; border-bottom: 1px solid #f1f1f1; } .quote-detail-label { color: #6c757d; font-weight: 600; flex-shrink: 0; } .quote-detail-value { text-align: right; word-break: break-word; } .quote-detail-status { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 0.5rem; } .quote-detail-status .form-select { width: auto; min-width: 7.5rem; } .quote-detail-status-note { flex-basis: 100%; text-align: right; color: #6c757d; font-size: 0.75rem; } /* Footer Buttons */ .quote-detail-footer { display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem; padding: 1rem 1.25rem; border-top: 1px solid #e9ecef; flex-wrap: wrap; } .quote-detail-footer .btn { flex: 0 0 auto; width: auto !important; min-width: 108px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; } .quote-detail-footer .btn svg { flex-shrink: 0; } @media (max-width: 650px) { .quote-detail-panel { width: 100%; } .quote-detail-footer { gap: 0.5rem; padding: 0.75rem; } } `} </style>
    </>
  );
};
